import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Chip, Typography, Drawer, CircularProgress
} from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchApprovedExpensesGrouped, fetchExpenseCategories } from '../../store/expenseStore/expenseThunk';
import { Utils } from '../../utils/common-utils';
import DialogExpenseSettlementForm from '../../components/Expenses/DialogExpenseSettlementForm';
import ExpenseFilter from '../../components/Expenses/ExpenseFilter';
import { ExpenseFilters } from '../../utils/expense.types';
import moment from 'moment';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#e9ecf1',
    color: '#545454',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: '6px 12px',
    fontSize: 14,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: 'normal',
    color: '#292d35',
    padding: '6px 12px',
    border: '0.75px solid #d9d4d4',
    backgroundColor: '#fff',
    margin: 0,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: '#fff',
}));

const ExpenseSettlement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { categories } = useAppSelector(state => state.expense);

  const [loading, setLoading] = useState(false);
  const [allExpenses, setAllExpenses] = useState<any[]>([]);
  const [settleDrawerOpen, setSettleDrawerOpen] = useState(false);
  const [settleExpenses, setSettleExpenses] = useState<any[]>([]);
  const [settleParty, setSettleParty] = useState<any>(null);
  const [filters, setFilters] = useState<ExpenseFilters>({});

  const loadData = async () => {
    setLoading(true);
    try {
      const result: any = await dispatch(fetchApprovedExpensesGrouped()).unwrap();
      const grps = result?.groups || [];
      const flat: any[] = [];
      grps.forEach((g: any) => {
        (g.expenses || []).forEach((exp: any) => {
          flat.push({ ...exp, partyName: g.partyName, partyType: g.partyType, partyId: g.partyId });
        });
      });
      setAllExpenses(flat);
    } catch (err) {
      console.error('Error loading approved expenses:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    dispatch(fetchExpenseCategories({ flat: false }));
  }, []);

  const handleFiltersChange = useCallback((newFilters: ExpenseFilters) => {
    setFilters(newFilters);
  }, []);

  const filteredExpenses = allExpenses.filter(e => {
    if (filters.vendor && e.partyId !== filters.vendor) return false;
    if (filters.employee && e.partyId !== filters.employee) return false;
    if (filters.expenseCategory && e.expenseCategory?._id !== filters.expenseCategory) return false;
    if (filters.dateFrom && new Date(e.paymentDate) < new Date(filters.dateFrom)) return false;
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(e.paymentDate) > to) return false;
    }
    return true;
  });

  const totalApproved = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  const getPartyChip = (type: string) => {
    if (type === 'account') return <Chip label="Account" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#e0f2f1', color: '#00695c' }} />;
    if (type === 'employee') return <Chip label="Employee" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#e3f2fd', color: '#1565c0' }} />;
    return <Chip label="Vendor" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#edf6ef', color: '#49a563' }} />;
  };

  return (
    <Box>
      {/* Filter Bar + Settle Now Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <ExpenseFilter
            onFiltersChange={handleFiltersChange}
            currentFilters={filters}
            categories={categories || []}
          />
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<PaymentIcon />}
          onClick={() => { setSettleExpenses(allExpenses); setSettleParty(null); setSettleDrawerOpen(true); }}
          sx={{ textTransform: 'none', fontSize: '0.8rem', height: 32, whiteSpace: 'nowrap', backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
        >
          Settle Now
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {!loading && filteredExpenses.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <AccountBalanceIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body1">No approved expenses pending settlement</Typography>
        </Box>
      )}

      {!loading && filteredExpenses.length > 0 && (
        <Table stickyHeader sx={{ minWidth: 700 }} size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell>Party & Reference</StyledTableCell>
              <StyledTableCell>Category</StyledTableCell>
              <StyledTableCell>Amount</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredExpenses.map((row: any) => (
              <StyledTableRow key={row._id}>
                <StyledTableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                    <span style={{ fontWeight: 500 }}>{moment(row.paymentDate).format("DD MMM YYYY")}</span>
                    <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: 12 }}>{moment(row.paymentDate).format("hh:mm A")}</span>
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                    <span style={{ fontWeight: 500 }}>{row.partyName}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                      {getPartyChip(row.partyType)}
                      <Chip label={row.referenceId || (row.txnId ? `EXP-${String(row.txnId).padStart(3, '0')}` : '')} size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#f5f3ff', color: '#6366f1', fontWeight: 400 }} />
                    </div>
                  </div>
                </StyledTableCell>
                <StyledTableCell>{row.expenseCategory?.name || '-'}</StyledTableCell>
                <StyledTableCell><span style={{ fontWeight: 600 }}>{Utils.formatNumber(parseFloat(row.amount).toFixed(2))}</span></StyledTableCell>
                <StyledTableCell>
                  <Chip label="Approved" size="small" style={{ fontSize: 13, height: '24px', fontWeight: 600, backgroundColor: '#e3f2fd', color: '#1565c0', borderRadius: '4px' }} />
                </StyledTableCell>
                <StyledTableCell>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PaymentIcon />}
                    onClick={() => {
                      const partyExpenses = allExpenses.filter(e => e.partyId === row.partyId);
                      setSettleExpenses(partyExpenses);
                      setSettleParty({ _id: row.partyId, name: row.partyName, legalName: row.partyName, _type: row.partyType });
                      setSettleDrawerOpen(true);
                    }}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', height: 28, backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
                  >
                    Settle Now
                  </Button>
                </StyledTableCell>
              </StyledTableRow>
            ))}
            <TableRow sx={{ backgroundColor: '#e9ecf1' }}>
              <StyledTableCell sx={{ fontWeight: 600, backgroundColor: '#e9ecf1' }}>Total</StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: '#e9ecf1' }} />
              <StyledTableCell sx={{ backgroundColor: '#e9ecf1' }} />
              <StyledTableCell sx={{ fontWeight: 700, backgroundColor: '#e9ecf1', color: '#d32f2f', fontSize: 15 }}>
                {Utils.formatNumber(totalApproved.toFixed(2))}
              </StyledTableCell>
              <StyledTableCell sx={{ fontWeight: 600, backgroundColor: '#e9ecf1' }}>{filteredExpenses.length} expenses</StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: '#e9ecf1' }} />
            </TableRow>
          </TableBody>
        </Table>
      )}

      {/* Settlement Form Drawer */}
      <Drawer disableEscapeKeyDown={false} anchor="right" open={settleDrawerOpen}>
        {settleDrawerOpen && (
          <DialogExpenseSettlementForm
            open={settleDrawerOpen}
            expenses={settleExpenses}
            partyName=""
            partyType=""
            preSelectedParty={settleParty}
            onClose={(settled) => { setSettleDrawerOpen(false); setSettleParty(null); if (settled) loadData(); }}
          />
        )}
      </Drawer>
    </Box>
  );
};

export default ExpenseSettlement;
